import { useMemo } from "react"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowLeftRight, SortAsc, SortDesc } from "lucide-react"
import { DateTime } from "luxon"

import { Button } from "@/components/ui/button.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import { numberFmt } from "@/util/numberFmt.ts"

import { getTripOverviewDates } from "../util/getTripOverviewDates.ts"

import type { TripCategory } from "../types/logisticsTypes.ts"

const columnHelper = createColumnHelper<TripCategory>()

export default function TripOverviewTable({
  data,
  isLoading,
  category,
  showCount,
  setCategory,
  setShowCount,
}: {
  data: TripCategory[]
  isLoading: boolean
  category: "farms" | "drivers"
  showCount: boolean
  setCategory: (c: "farms" | "drivers") => void
  setShowCount: (sc: boolean) => void
}) {
  const columnDefs = useMemo(() => {
    const nameCol = columnHelper.accessor((farm) => farm.name, {
      id: "names",
      header: () => (
        <div className="p-2 md:w-[200px]">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => setCategory(category === "farms" ? "drivers" : "farms")}
          >
            {category == "farms" ? "Farms" : "Drivers"}
            <ArrowLeftRight />
          </Button>
        </div>
      ),
      footer: () => (
        <div className="px-2">
          <Button
            variant="ghost"
            className="w-full justify-start"
            onClick={() => setShowCount(!showCount)}
          >
            Total {showCount ? "Loads" : "Gallons"}
            <ArrowLeftRight />
          </Button>
        </div>
      ),
      cell: (context) => <div className="px-4">{context.getValue()}</div>,
    })

    const dayCols = getTripOverviewDates(7).map((d) =>
      columnHelper.accessor(
        (farm) => {
          const t = farm.trips?.find(
            (trip) =>
              DateTime.fromISO(trip.start || "").toISODate() == DateTime.fromJSDate(d).toISODate(),
          )

          if (t) {
            return showCount ? t.count : t.unloadVolume || 0
          }
        },
        {
          id: d.toDateString(),
          header: () => (
            <div className="p-2 text-center">
              <span className="hidden md:inline">{DateTime.fromJSDate(d).toFormat("ccc L/d")}</span>
              <span className="inline md:hidden">{DateTime.fromJSDate(d).toFormat("L/d")}</span>
            </div>
          ),
          footer: ({ table }) => (
            <div className="text-center px-2 py-3">
              {numberFmt(
                table
                  .getRowModel()
                  .rows.reduce(
                    (sum, row) => sum + (row.getValue<number>(d.toDateString()) || 0),
                    0,
                  ),
              )}
            </div>
          ),
          cell: (context) => (
            <div className="text-center">{numberFmt(context.getValue() || 0)}</div>
          ),
        },
      ),
    )
    const avgCol = columnHelper.accessor(
      (cat) => {
        const sum = cat.trips
          ?.slice(0, 7)
          .reduce((a, c) => a + ((showCount ? c.count : c.unloadVolume) || 0), 0)
        return (sum || 0) / 7
      },
      {
        id: "average",
        header: ({ column }) => (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-[100px] flex"
            >
              7-Day AVG
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => (
          <div className="text-center py-2 font-semibold border-x-1">
            {numberFmt(context.getValue() || 0)}
          </div>
        ),
        footer: ({ table }) => (
          <div className="text-center py-3  font-semibold">
            {numberFmt(
              table
                .getRowModel()
                .rows.reduce((sum, row) => sum + row.getValue<number>("average"), 0),
            )}
          </div>
        ),
      },
    )
    const mtdCol = columnHelper.accessor(
      (cat) =>
        showCount
          ? cat.trips?.at(cat.trips.length - 1)?.count
          : cat.trips?.at(cat.trips.length - 1)?.unloadVolume,
      {
        id: "mtd",
        header: ({ column }) => (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-[100px] flex"
            >
              MTD
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => (
          <div className="text-center py-2 font-semibold">{numberFmt(context.getValue() || 0)}</div>
        ),
        footer: ({ table }) => (
          <div className="text-center font-semibold">
            {numberFmt(
              table.getRowModel().rows.reduce((sum, row) => sum + row.getValue<number>("mtd"), 0),
            )}
          </div>
        ),
      },
    )

    const todayCol = columnHelper.accessor(
      (cat) =>
        showCount
          ? cat.trips?.at(cat.trips.length - 2)?.count
          : cat.trips?.at(cat.trips.length - 2)?.unloadVolume,
      {
        id: "today",
        header: ({ column }) => (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              onClick={column.getToggleSortingHandler()}
              className="w-[100px] flex"
            >
              Today
              <div>
                {column.getNextSortingOrder() == false && <SortAsc />}
                {column.getNextSortingOrder() == "asc" && <SortDesc />}
              </div>
            </Button>
          </div>
        ),
        cell: (context) => (
          <div className="text-center font-semibold  border-l-1 py-2">
            {numberFmt(context.getValue() || 0)}
          </div>
        ),
        footer: ({ table }) => (
          <div className="text-center font-semibold">
            {numberFmt(
              table.getRowModel().rows.reduce((sum, row) => sum + row.getValue<number>("today"), 0),
            )}
          </div>
        ),
      },
    )

    return [nameCol, ...dayCols, avgCol, mtdCol, todayCol]
  }, [category, setCategory, showCount, setShowCount])

  const table = useReactTable<TripCategory>({
    data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <Table>
      {/* HEADER */}
      {table.getHeaderGroups().map((hg, i) => (
        <TableHeader key={i} className="sticky top-0 z-10 bg-card-header">
          <TableRow
            className="
                  [&_th:first-child]:rounded-tl-md
                  [&_th:last-child]:rounded-tr-md
                "
          >
            {hg.headers.map((header, j) => (
              <TableHead key={j}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}

      {/* BODY */}
      <TableBody>
        {isLoading &&
          [...Array(10).keys()].map((i) => (
            <TableRow key={i}>
              <TableCell colSpan={11}>
                <Skeleton className="w-full h-[37px]" />
              </TableCell>
            </TableRow>
          ))}
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell, j) => (
              <TableCell key={j}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>

      {/* FOOTER */}
      {table.getFooterGroups().map((fg, i) => (
        <TableFooter key={i} className="sticky bottom-0 z-10 bg-muted">
          <TableRow
            className="
                  [&_td:first-child]:rounded-bl-md
                  [&_td:last-child]:rounded-br-md
                "
          >
            {fg.headers.map((header, j) => (
              <TableCell key={j} className="py-1">
                {flexRender(header.column.columnDef.footer, header.getContext())}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      ))}
    </Table>
  )
}
